// Note: code is taken from use-hooks-ts (https://usehooks-ts.com/react-hook/use-intersection-observer)
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";

/** The hook internal state. */
type State = {
  /** A boolean indicating if the element is intersecting. */
  isIntersecting: boolean;
  /** The intersection observer entry. */
  entry?: IntersectionObserverEntry;
};

/** Represents the options for configuring the Intersection Observer. */
type UseIntersectionObserverOptions = {
  /**
   * The element that is used as the viewport for checking visibility of the target.
   * @default null
   */
  root?: Element | Document | null;
  /**
   * A margin around the root.
   * @default '0%'
   */
  rootMargin?: string;
  /**
   * A threshold indicating the percentage of the target's visibility needed to trigger the callback.
   * @default 0
   */
  threshold?: number | number[];
  /**
   * If true, freezes the intersection state once the element becomes visible.
   * @default false
   */
  freezeOnceVisible?: boolean;
  /**
   * Optional pool name for sharing a single IntersectionObserver across
   * multiple hook instances with identical observer options.
   * @default undefined
   */
  pool?: string;
  /**
   * A callback function to be invoked when the intersection state changes.
   * @param {boolean} isIntersecting - A boolean indicating if the element is intersecting.
   * @param {IntersectionObserverEntry} entry - The intersection observer Entry.
   * @default undefined
   */
  onChange?: (
    isIntersecting: boolean,
    entry: IntersectionObserverEntry,
  ) => void;
  /**
   * The initial state of the intersection.
   * @default false
   */
  initialIsIntersecting?: boolean;
};

/**
 * The return type of the useIntersectionObserver hook.
 *
 * Supports both tuple and object destructing.
 * @param {(node: HTMLElement | null) => void} ref - The ref callback function.
 * @param {boolean} isIntersecting - A boolean indicating if the element is intersecting.
 * @param {IntersectionObserverEntry | undefined} entry - The intersection observer Entry.
 */
type IntersectionReturnArray = [
  Dispatch<SetStateAction<HTMLElement | null>>,
  boolean,
  IntersectionObserverEntry | undefined,
];
type IntersectionReturnObject = {
  ref: Dispatch<SetStateAction<HTMLElement | null>>;
  isIntersecting: boolean;
  entry?: IntersectionObserverEntry;
  element: HTMLElement | null;
};

type IntersectionReturn = IntersectionReturnArray & IntersectionReturnObject;

type ObserverPoolItem = {
  observer: IntersectionObserver;
  handlersByElement: Map<
    Element,
    Set<(entry: IntersectionObserverEntry) => void>
  >;
  handlerCount: number;
  options: {
    root: Element | Document | null;
    rootMargin: string;
    thresholdKey: string;
  };
};

const observerPool = new Map<string, ObserverPoolItem>();

const thresholdToKey = (threshold: number | number[]) => {
  return Array.isArray(threshold) ? threshold.join(",") : String(threshold);
};

const getPooledObserver = ({
  pool,
  root,
  rootMargin,
  threshold,
}: {
  pool: string;
  root: Element | Document | null;
  rootMargin: string;
  threshold: number | number[];
}) => {
  const thresholdKey = thresholdToKey(threshold);
  const existingPool = observerPool.get(pool);

  if (existingPool) {
    const optionsMatch =
      existingPool.options.root === root &&
      existingPool.options.rootMargin === rootMargin &&
      existingPool.options.thresholdKey === thresholdKey;

    if (!optionsMatch) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `[useIntersectionObserver] pool "${pool}" was requested with different observer options. Falling back to non-pooled observer for this instance.`,
        );
      }
      return null;
    }

    return existingPool;
  }

  const handlersByElement = new Map<
    Element,
    Set<(entry: IntersectionObserverEntry) => void>
  >();

  const nextPool: ObserverPoolItem = {
    observer: new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        for (const entry of entries) {
          const handlers = handlersByElement.get(entry.target);
          if (!handlers) continue;
          handlers.forEach((handler) => handler(entry));
        }
      },
      { threshold, root, rootMargin },
    ),
    handlersByElement,
    handlerCount: 0,
    options: {
      root,
      rootMargin,
      thresholdKey,
    },
  };

  observerPool.set(pool, nextPool);
  return nextPool;
};

/**
 * Custom hook that tracks the intersection of a DOM element with its containing element or the viewport.
 * @param {UseIntersectionObserverOptions} options - The options for the Intersection Observer.
 * @returns {IntersectionReturn} The ref callback, a boolean indicating if the element is intersecting, and the intersection observer entry.
 * @public
 * @see [Documentation](https://usehooks-ts.com/react-hook/use-intersection-observer)
 * @see [MDN Intersection Observer API](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API)
 * @example
 * ```tsx
 * // Example 1
 * const [ref, isIntersecting, entry] = useIntersectionObserver({ threshold: 0.5 });
 * ```
 *
 * ```tsx
 * // Example 2
 * const { ref, isIntersecting, entry } = useIntersectionObserver({ threshold: 0.5 });
 * ```
 */
export function useIntersectionObserver({
  threshold = 0,
  root = null,
  rootMargin = "0%",
  freezeOnceVisible = false,
  initialIsIntersecting = false,
  pool,
  onChange,
}: UseIntersectionObserverOptions = {}): IntersectionReturn {
  const [ref, setRef] = useState<HTMLElement | null>(null);

  const [state, setState] = useState<State>(() => ({
    isIntersecting: initialIsIntersecting,
    entry: undefined,
  }));

  const callbackRef = useRef<UseIntersectionObserverOptions["onChange"]>(
    undefined as never,
  );

  callbackRef.current = onChange;

  const frozen = state.entry?.isIntersecting && freezeOnceVisible;

  useEffect(() => {
    // Ensure we have a ref to observe
    if (!ref) return;

    // Ensure the browser supports the Intersection Observer API
    if (!("IntersectionObserver" in window)) return;

    // Skip if frozen
    if (frozen) return;

    let unobserve: (() => void) | undefined;
    const pooledObserver =
      pool &&
      getPooledObserver({
        pool,
        root,
        rootMargin,
        threshold,
      });

    if (pooledObserver) {
      const element = ref;
      const thresholds = Array.isArray(pooledObserver.observer.thresholds)
        ? pooledObserver.observer.thresholds
        : [pooledObserver.observer.thresholds];

      const entryHandler = (entry: IntersectionObserverEntry) => {
        const isIntersecting =
          entry.isIntersecting &&
          thresholds.some((threshold) => entry.intersectionRatio >= threshold);

        setState({ isIntersecting, entry });

        if (callbackRef.current) {
          callbackRef.current(isIntersecting, entry);
        }

        if (isIntersecting && freezeOnceVisible && unobserve) {
          unobserve();
        }
      };

      const elementHandlers =
        pooledObserver.handlersByElement.get(element) ?? new Set();

      if (!pooledObserver.handlersByElement.has(element)) {
        pooledObserver.handlersByElement.set(element, elementHandlers);
        pooledObserver.observer.observe(element);
      }

      elementHandlers.add(entryHandler);
      pooledObserver.handlerCount += 1;

      let isUnobserved = false;
      unobserve = () => {
        if (isUnobserved) return;
        isUnobserved = true;

        const handlers = pooledObserver.handlersByElement.get(element);
        if (handlers?.has(entryHandler)) {
          handlers.delete(entryHandler);
          pooledObserver.handlerCount -= 1;
        }

        if (handlers && handlers.size === 0) {
          pooledObserver.handlersByElement.delete(element);
          pooledObserver.observer.unobserve(element);
        }

        if (pooledObserver.handlerCount <= 0 && pool) {
          pooledObserver.observer.disconnect();
          observerPool.delete(pool);
        }
      };

      return () => {
        unobserve?.();
      };
    }

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        const thresholds = Array.isArray(observer.thresholds)
          ? observer.thresholds
          : [observer.thresholds];

        entries.forEach((entry) => {
          const isIntersecting =
            entry.isIntersecting &&
            thresholds.some(
              (threshold) => entry.intersectionRatio >= threshold,
            );

          setState({ isIntersecting, entry });

          if (callbackRef.current) {
            callbackRef.current(isIntersecting, entry);
          }

          if (isIntersecting && freezeOnceVisible && unobserve) {
            unobserve();
          }
        });
      },
      { threshold, root, rootMargin },
    );

    observer.observe(ref);

    let isUnobserved = false;
    unobserve = () => {
      if (isUnobserved) return;
      isUnobserved = true;
      observer.disconnect();
    };

    return () => {
      unobserve?.();
    };
  }, [
    ref,
    JSON.stringify(threshold),
    root,
    rootMargin,
    pool,
    frozen,
    freezeOnceVisible,
  ]);

  // ensures that if the observed element changes, the intersection observer is reinitialized
  const prevRef = useRef<Element | null>(null);

  useEffect(() => {
    if (
      !ref &&
      state.entry?.target &&
      !freezeOnceVisible &&
      !frozen &&
      prevRef.current !== state.entry.target
    ) {
      prevRef.current = state.entry.target;
      setState({ isIntersecting: initialIsIntersecting, entry: undefined });
    }
  }, [ref, state.entry, freezeOnceVisible, frozen, initialIsIntersecting]);

  const result: IntersectionReturnArray = [
    setRef,
    state.isIntersecting,
    state.entry,
  ];

  // Support object destructuring, by adding the specific values.
  return Object.assign(result, {
    ref: setRef,
    element: ref,
    isIntersecting: state.isIntersecting,
    entry: state.entry,
  });
}
